/**
 * TagsCell - shared tags column renderer for the CRM tables
 * (Dashboard contacts table and My Deals table).
 *
 * Shows the first `maxDisplay` tags as purple badges with a gray "+N"
 * overflow badge, and a hover tooltip listing ALL of the row's tags.
 */
import React from 'react';
import {
  Badge,
  HStack,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';

export default function TagsCell({ tags = [], maxDisplay = 2 }) {
  // Chakra's default tooltip inverts against the color mode (dark surface in
  // light mode, light surface in dark mode), which makes the badge pills
  // unreadable in dark mode. Pin the surface to the app's mode instead.
  const tooltipBg = useColorModeValue('white', 'gray.700');
  const tooltipColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  if (!tags || tags.length === 0) {
    return <Text fontSize="sm" color="gray.400">—</Text>;
  }

  const allTagsLabel = (
    <Wrap spacing={1} py={1} maxW="320px">
      {tags.map((tag) => (
        <WrapItem key={tag}>
          <Badge colorScheme="purple">{tag}</Badge>
        </WrapItem>
      ))}
    </Wrap>
  );

  return (
    <Tooltip
      label={allTagsLabel}
      hasArrow
      placement="top"
      openDelay={150}
      bg={tooltipBg}
      color={tooltipColor}
      boxShadow="lg"
    >
      <HStack spacing={1} flexWrap="wrap" w="100%">
        {tags.slice(0, maxDisplay).map((tag) => (
          <Badge key={tag} colorScheme="purple">
            {tag}
          </Badge>
        ))}
        {tags.length > maxDisplay && (
          <Badge colorScheme="gray">+{tags.length - maxDisplay}</Badge>
        )}
      </HStack>
    </Tooltip>
  );
}
